import React from 'react';
import { Row, Col, Card, List, Avatar, Tag, Space, Typography, Button } from 'antd';
import {
  ShoppingCartOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Activity, PendingApproval } from '@/services/dashboard.service';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

const { Text } = Typography;

function activityIcon(type: string) {
  switch (type) {
    case 'sale':
      return <ShoppingCartOutlined style={{ color: '#145A54' }} />;
    case 'invoice':
      return <DollarOutlined style={{ color: '#06D6A0' }} />;
    case 'project':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'purchase':
      return <FileTextOutlined style={{ color: '#d46b08' }} />;
    default:
      return <FileTextOutlined style={{ color: '#8c8c8c' }} />;
  }
}

export interface ExecutiveActivityPanelsProps {
  activities: Activity[];
  pendingApprovals: PendingApproval[];
}

export const ExecutiveActivityPanels: React.FC<ExecutiveActivityPanelsProps> = ({
  activities,
  pendingApprovals,
}) => {
  const navigate = useNavigate();

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Col xs={24} lg={12}>
        <Card
          title="Recent activity"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          extra={
            <Button type="link" size="small" onClick={() => navigate('/unified')}>
              Workboard
            </Button>
          }
        >
          {activities.length === 0 ? (
            <Text type="secondary">No recent audit events. Activity will appear as users work in the system.</Text>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={activities}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={activityIcon(item.type)} />}
                    title={item.action}
                    description={
                      <>
                        <div>{item.description}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.time}
                        </Text>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          title="Summary approvals queue"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          extra={
            <Button type="link" size="small" onClick={() => navigate('/unified')}>
              Unified view
            </Button>
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            High-level counts from the dashboard service. Use the unified workboard for live document-level queues.
          </Text>
          <List
            dataSource={pendingApprovals}
            locale={{ emptyText: 'No pending approval buckets reported.' }}
            renderItem={(item) => (
              <List.Item>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <WarningOutlined
                      style={{
                        color:
                          item.priority === 'high' ? '#cf1322' : item.priority === 'medium' ? '#d46b08' : '#8c8c8c',
                      }}
                    />
                    <Text>{item.type}</Text>
                  </Space>
                  <Tag color="blue">{item.count} pending</Tag>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      </Col>
    </Row>
  );
};
